import React from 'react';
import PropTypes from 'prop-types';
import paginationFactory, { PaginationProvider } from 'react-bootstrap-table2-paginator';
import BootstrapTable from 'react-bootstrap-table-next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TablePaginationFooter from './TablePaginationFooter';

const defaultNoDataIndication = (
  <div className="med-dashboard-empty">Ma'lumot topilmadi.</div>
);

const sortCaret = (order, column) => {
  if (!column.sort) {
    return null;
  }

  if (!order) {
    return <FontAwesomeIcon icon="sort" className="ml-2 text-400 med-table-sort-icon" />;
  }

  return (
    <FontAwesomeIcon
      icon={order === 'asc' ? 'sort-alpha-up' : 'sort-alpha-down'}
      className="ml-2 text-primary med-table-sort-icon"
    />
  );
};

const MedDataTable = ({
  data,
  columns,
  keyField,
  pageSize,
  paginationResetKey,
  noDataIndication,
  tableClasses,
  rowClasses,
  headerClasses,
  wrapperClasses,
  defaultSorted
}) => {
  const options = {
    custom: true,
    sizePerPage: pageSize,
    totalSize: data.length,
    pageStartIndex: 1,
    hideSizePerPage: true,
    paginationSize: 5
  };

  if (!Array.isArray(columns) || columns.length === 0) {
    return <div className={wrapperClasses}>{noDataIndication}</div>;
  }

  return (
    <PaginationProvider key={`${pageSize}-${paginationResetKey}-${data.length}`} pagination={paginationFactory(options)}>
      {({ paginationProps, paginationTableProps }) => (
        <>
          <BootstrapTable
            bootstrap4
            keyField={keyField}
            data={data}
            columns={columns.map(column => ({
              ...column,
              sortCaret: column.sort ? sortCaret : column.sortCaret
            }))}
            bordered={false}
            wrapperClasses={wrapperClasses}
            classes={tableClasses}
            rowClasses={rowClasses}
            headerClasses={headerClasses}
            defaultSorted={defaultSorted}
            noDataIndication={noDataIndication}
            {...paginationTableProps}
          />
          <TablePaginationFooter
            totalItems={paginationProps.totalSize}
            currentPage={paginationProps.page}
            pageSize={paginationProps.sizePerPage}
            onPageChange={paginationProps.onPageChange}
          />
        </>
      )}
    </PaginationProvider>
  );
};

MedDataTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  keyField: PropTypes.string,
  pageSize: PropTypes.number,
  paginationResetKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  noDataIndication: PropTypes.node,
  tableClasses: PropTypes.string,
  rowClasses: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  headerClasses: PropTypes.string,
  wrapperClasses: PropTypes.string,
  defaultSorted: PropTypes.arrayOf(PropTypes.object)
};

MedDataTable.defaultProps = {
  data: [],
  keyField: 'id',
  pageSize: 20,
  paginationResetKey: '',
  noDataIndication: defaultNoDataIndication,
  tableClasses: 'table table-hover fs--1 mb-0 med-dashboard-table',
  rowClasses: '',
  headerClasses: 'bg-200 text-900',
  wrapperClasses: 'table-responsive',
  defaultSorted: []
};

export default MedDataTable;
